import { TagDescription } from '@reduxjs/toolkit/query';
import { CONSTANT_TASK_LISTING_PAGE_LIMIT } from 'utils/constants';
import { baseApi } from './baseApi';
import {
  AllTaskResponse,
  FilterData,
  FilterResponseData,
  ProjectResponse,
  TaskAttributes,
  TaskResponse,
} from './types/tasks';

export type FetchTaskParams = {
  offset?: number;
  limit?: number;
  search_keyword?: string;
  userId?: string;
  filter_by?: string;
  site_order?: string;
  workspace_id?: number;
  order_by?: string;
  type?: string;
  apply_filter?: boolean;
  from?: string;
};

export type BulkMethods =
  | 'status'
  | 'priority'
  | 'notify_wpfeedback_users'
  | 'task_notify_users'
  | 'notify_users'
  | 'task_title'
  | 'task_tags'
  | 'delete_tags'
  | 'reconnect_task'
  | 'pin'
  | 'unpin';

// the union is because taskId's / websiteId's are required
export type BulkUpdateParams = {
  taskId?: string;
  taskIds?: string[];
  websiteId?: string;
  websiteIds?: string[] | Set<string>;
  value?: any;
  updateMethod?: BulkMethods;
  internal?: number;
  is_delete?: boolean;
  id?: string | number;
} & ({ taskId: string } | { taskIds: string[] }) &
  ({ websiteId: string } | { websiteIds: string[] | Set<string> });

interface AttributesApiResponse<T> {
  result: T;
  status: number;
}

export const taskApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    fetchSiteList: build.query<ProjectResponse, Partial<FetchTaskParams>>({
      queryFn: async (params = {}, _1, _2, baseQuery) => {
        const searchParams = new URLSearchParams();
        const defaultParams: FetchTaskParams = {
          offset: 0,
          limit: 20,
          search_keyword: '',
          userId: '',
          filter_by: '',
          site_order: '',
          workspace_id: 0,
          ...params,
        };

        Object.entries(defaultParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) =>
              searchParams.append(`${key}[]`, item.toString())
            );
          } else {
            if (value !== undefined && value !== null) {
              searchParams.append(key, value.toString());
            }
          }
        });

        const url = `/site?${searchParams.toString()}`;
        const result = await baseQuery(url);

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as ProjectResponse };
      },
      providesTags: [{ type: 'Site', id: 'LIST' }],
    }),
    fetchAllTasks: build.query<AllTaskResponse, FetchTaskParams>({
      queryFn: async (params = {}, _1, _2, baseQuery) => {
        const searchParams = new URLSearchParams();
        const defaultParams: FetchTaskParams = {
          offset: 0,
          limit: CONSTANT_TASK_LISTING_PAGE_LIMIT,
          type: 'task',
          workspace_id: 0,
          ...params,
        };

        Object.entries(defaultParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) =>
              searchParams.append(`${key}[]`, item.toString())
            );
          } else if (key === 'order_by' && typeof value === 'string') {
            if (!value.includes(':')) throw new Error('Invalid order_by value');
            let [sortField, sortOrder = 'desc'] = value.split(':');
            searchParams.append('order_by', sortField);
            searchParams.append('direction', sortOrder);

          } else {
            if (value !== undefined && value !== null) {
              searchParams.append(key, value.toString());
            }
          }
        });

        const url = `/task/get-all-tasks?${searchParams.toString()}`;
        const result = await baseQuery(url);

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AllTaskResponse };
      },
      providesTags: [{ type: 'Task', id: 'LIST' }],
    }),
    fetchTaskList: build.query<
      TaskResponse,
      { id: string; query?: Partial<FetchTaskParams> }
    >({
      query: (params = { id: '', query: {} }) => {
        const searchParams = new URLSearchParams();
        const { id, query } = params;
        const defaultParams: FetchTaskParams = {
          offset: 0,
          limit: CONSTANT_TASK_LISTING_PAGE_LIMIT,
          apply_filter: true,
          ...query,
        };
        Object.entries(defaultParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) =>
              searchParams.append(`${key}[]`, item.toString())
            );
          } else if (key === 'order_by' && typeof value === 'string') {
            if (!value.includes(':')) throw new Error('Invalid order_by value');
            let [sortField, sortOrder = 'desc'] = value.split(':');
            searchParams.append('order_by', sortField);
            searchParams.append('direction', sortOrder);
          } else {
            if (value !== undefined && value !== null) {
              searchParams.append(key, value.toString());
            }
          }
        });

        return `/site/tasks/${id}?${searchParams.toString()}`;
      },
      providesTags: (_, error, arg) => {
        if (error) return [];
        return [
          { type: 'Task', id: 'LIST' },
          { type: 'Task', id: arg.id },
        ];
      },
    }),
    fetchTaskAttributes: build.query<TaskAttributes, string>({
      query: (id: string) => `/collaborate/tasks/${id}`,
      transformResponse: (response: AttributesApiResponse<TaskAttributes>) =>
        response.result,
      providesTags: (_, error, id) => {
        if (error) return [];
        return [{ type: 'Attribute', id }];
      },
    }),
    fetchAllFilterData: build.query<FilterData, string | number>({
      query: (workspaceId) =>
        `/site/get-filter-data?workspace_id=${workspaceId}`,
      transformResponse: (response: FilterResponseData<FilterData>) =>
        response.data,
      providesTags: (_, error) => {
        if (error) return [];
        return [{ type: 'Filter', id: 'LIST' }];
      },
    }),
    fetchTaskFilterData: build.query<any, string>({
      query: (id: string) => `/site/get-filter-data/${id}`,
      transformResponse: (response: { data: any }) => response.data,
      providesTags: (_, error, id) => {
        if (error) return [];
        return [{ type: 'Filter', id }];
      },
    }),

    // Mutations
    bulkUpdateTaskDetails: build.mutation<any, BulkUpdateParams>({
      query: ({
        taskId,
        taskIds,
        value,
        updateMethod,
        internal,
        is_delete,
        id,
      }) => {
        return {
          url: '/task/bulkupdate-task-details',
          method: 'POST',
          body: {
            method: updateMethod,
            task_id: taskId,
            task_ids: taskIds,
            value,
            internal,
            is_delete,
            id,
          },
        };
      },
      invalidatesTags: (
        _,
        error,
        { taskId, websiteId, websiteIds, updateMethod }
      ) => {
        if (error) return [];

        let invalidateTags: TagDescription<
          'Task' | 'Attribute' | 'Filter' | 'Site' | 'CollaborateTasks'
        >[] = [
          { type: 'Task', id: 'LIST' },
          { type: 'CollaborateTasks', id: 'LIST' },
        ];

        // Handle websiteId (single) and websiteIds (Set)
        const websiteIdsToInvalidate = new Set<string>();
        if (websiteId) websiteIdsToInvalidate.add(websiteId);
        if (websiteIds && websiteIds instanceof Set) {
          websiteIds.forEach((id) => websiteIdsToInvalidate.add(id));
        }

        websiteIdsToInvalidate.forEach((id) => {
          invalidateTags.push({ type: 'Site', id }, { type: 'Task', id });
        });

        // Handle taskId(s)
        if (taskId) {
          if (typeof taskId === 'string' && taskId.includes(',')) {
            taskId.split(',').forEach((id) => {
              invalidateTags.push({ type: 'Attribute', id });
              invalidateTags.push({ type: 'CollaborateTasks', id });
              invalidateTags.push({ type: 'Task', id });
            });
          } else {
            invalidateTags.push({ type: 'Attribute', id: taskId });
            invalidateTags.push({ type: 'CollaborateTasks', id: taskId });
            invalidateTags.push({ type: 'Task', id: taskId });
          }
        }

        // Handle updateMethod
        if (updateMethod === 'task_tags') {
          websiteIdsToInvalidate.forEach((id) => {
            invalidateTags.push({ type: 'Filter', id });
          });
        }

        return invalidateTags;
      },
    }),
    bulkDeleteTask: build.mutation<any, { taskId: string; websiteId?: string }>(
      {
        query: ({ taskId }) => {
          return {
            url: '/task/bulk-delete-task',
            method: 'POST',
            body: {
              task_id: taskId,
            },
          };
        },
        invalidatesTags: (_, error, { taskId, websiteId }) => {
          if (error) return [];
          return [
            { type: 'Task', id: 'LIST' },
            { type: 'CollaborateTasks', id: 'LIST' },
            { type: 'Site', id: 'LIST' },
            { type: 'Site', id: websiteId },
            { type: 'Task', id: taskId },
            { type: 'CollaborateTasks', id: taskId },
            { type: 'Attribute', id: taskId },
          ];
        },
      }
    ),
  }),
});

export const {
  useFetchSiteListQuery,
  useFetchAllTasksQuery,
  useFetchTaskListQuery,
  useLazyFetchTaskListQuery,
  useFetchTaskAttributesQuery,
  useFetchAllFilterDataQuery,
  useFetchTaskFilterDataQuery,
  // Mutations
  useBulkUpdateTaskDetailsMutation,
  useBulkDeleteTaskMutation,
} = taskApi;
